import { Controller } from "@hotwired/stimulus"

// Client-side enhancement of a post table: substring filter on the search input
// + type-aware column sorting via th[data-sort] / td[data-sort-value]. Self-contained;
// no server round-trip.
export default class extends Controller {
  static targets = ["input", "row"]

  connect() {
    this.bindSortHeaders()
  }

  // Filter rows by case-insensitive substring match against the row text.
  search() {
    const q = (this.hasInputTarget ? this.inputTarget.value : "").trim().toLowerCase()
    this.rowTargets.forEach((row) => {
      const match = q === "" || row.textContent.toLowerCase().includes(q)
      row.style.display = match ? "" : "none"
    })
  }

  // ---- sorting ----

  bindSortHeaders() {
    this.headers = Array.from(this.element.querySelectorAll("th[data-sort]"))
    this.headers.forEach((th, index) => {
      th.classList.add("select-none")
      th.style.cursor = "pointer"
      th.addEventListener("click", () => this.sortBy(th, index))
    })

    // Apply the declared default sort (e.g. 投稿日時 desc) on connect.
    const defaultTh = this.headers.find((th) => th.dataset.defaultSort)
    if (defaultTh) {
      const idx = this.headers.indexOf(defaultTh)
      const dir = defaultTh.dataset.defaultSort === "asc" ? "asc" : "desc"
      defaultTh.dataset.direction = dir
      this.applySort(defaultTh, idx, dir)
      this.updateIcons(defaultTh)
    }
  }

  sortBy(th, headerIndex) {
    const current = th.dataset.direction
    const dir = current === "asc" ? "desc" : "asc"
    this.headers.forEach((h) => {
      if (h !== th) delete h.dataset.direction
    })
    th.dataset.direction = dir
    this.applySort(th, headerIndex, dir)
    this.updateIcons(th)
  }

  // Find the matching <td> for this header by matching column position among
  // the cells that carry data-sort-value, falling back to absolute column index.
  applySort(th, headerIndex, dir) {
    const type = th.dataset.sort
    const colIndex = this.columnIndex(th)

    const rows = this.rowTargets.slice()
    rows.sort((a, b) => {
      const av = this.cellValue(a, colIndex)
      const bv = this.cellValue(b, colIndex)
      const cmp = this.compare(av, bv, type)
      return dir === "asc" ? cmp : -cmp
    })

    const tbody = rows.length ? rows[0].parentNode : null
    if (tbody) rows.forEach((row) => tbody.appendChild(row))
  }

  columnIndex(th) {
    const ths = Array.from(th.parentNode.children)
    return ths.indexOf(th)
  }

  cellValue(row, colIndex) {
    const cells = row.querySelectorAll("td")
    const cell = cells[colIndex]
    if (!cell) return ""
    const raw = cell.dataset.sortValue
    return raw !== undefined ? raw : cell.textContent.trim()
  }

  compare(a, b, type) {
    if (type === "number" || type === "percentage") {
      const na = parseFloat(String(a).replace(/[^0-9.\-]/g, ""))
      const nb = parseFloat(String(b).replace(/[^0-9.\-]/g, ""))
      const sa = isNaN(na) ? -Infinity : na
      const sb = isNaN(nb) ? -Infinity : nb
      return sa === sb ? 0 : sa < sb ? -1 : 1
    }
    if (type === "date") {
      const da = Date.parse(a)
      const db = Date.parse(b)
      if (!isNaN(da) && !isNaN(db)) return da === db ? 0 : da < db ? -1 : 1
      // fall back to locale-safe string compare
      return String(a).localeCompare(String(b))
    }
    return String(a).localeCompare(String(b))
  }

  // Reset every sort icon to neutral; mark the active header asc/desc.
  updateIcons(activeTh) {
    this.headers.forEach((th) => {
      const icon = th.querySelector(".sort-icon")
      if (!icon) return
      icon.classList.remove("lucide--chevron-up", "lucide--chevron-down", "lucide--chevrons-up-down")
      if (th === activeTh) {
        icon.classList.add(th.dataset.direction === "asc" ? "lucide--chevron-up" : "lucide--chevron-down")
      } else {
        icon.classList.add("lucide--chevrons-up-down")
      }
    })
  }
}
